// ==================== SMS SETTINGS PAGE ====================
// SMS Integration Settings with Pakistani + International Providers

import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { 
  MessageSquare, 
  Key,
  Shield,
  CheckCircle,
  AlertCircle,
  Save,
  Eye,
  EyeOff,
  ExternalLink,
  Copy,
  Check,
  Link2,
  AlertTriangle,
  Unplug,
  Zap,
  Globe,
  Server,
  Phone,
  Settings,
  TestTube,
  Info,
  Loader2,
} from 'lucide-react';
import { Button, Input, Badge } from '../../../components/ui';
import {
  getSmsAccount,
  createSmsAccount,
  updateSmsAccount,
  deleteSmsAccount,
  testSmsConnection,
} from '../../../services/sms.service';
import type {
  SmsAccount as SmsAccountType,
  SmsProviderType as ApiSmsProviderType,
} from '../../../services/sms.service';

// ==================== TYPES ====================

// Pakistani + International Providers
type SmsProviderType = 
  // Pakistani Providers
  | 'EOCEAN' | 'JAZZ' | 'TELENOR' | 'UFONE' | 'ZONG' 
  | 'JEEO_SMS' | 'OUTREACH' | 'SMS4CONNECT' | 'BROADNET'
  // International Providers
  | 'TWILIO' | 'VONAGE' | 'MESSAGEBIRD' | 'PLIVO' | 'SINCH' | 'INFOBIP'
  // Custom
  | 'CUSTOM';

interface SmsAccount {
  id: string;
  provider: SmsProviderType;
  apiKey: string;
  senderId: string;
  baseUrl?: string;
  status: 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'DISCONNECTED';
  createdAt: string;
  updatedAt: string;
}

// ==================== VALIDATION SCHEMA ====================

const smsSchema = z.object({
  provider: z.enum([
    'EOCEAN', 'JAZZ', 'TELENOR', 'UFONE', 'ZONG', 
    'JEEO_SMS', 'OUTREACH', 'SMS4CONNECT', 'BROADNET',
    'TWILIO', 'VONAGE', 'MESSAGEBIRD', 'PLIVO', 'SINCH', 'INFOBIP',
    'CUSTOM'
  ]),
  apiKey: z.string().min(1, 'API Key is required'),
  apiSecret: z.string().optional(),
  accountSid: z.string().optional(),
  senderId: z.string().min(1, 'Sender ID is required').max(11, 'Sender ID must be 11 characters or less'),
  baseUrl: z.string().url('Must be a valid URL').optional().or(z.literal('')),
});

type SmsFormData = z.infer<typeof smsSchema>;

// ==================== STATUS BADGE COMPONENT ====================

const StatusBadge = ({ status }: { status: SmsAccount['status'] | undefined }) => {
  const config: Record<string, { variant: 'success' | 'warning' | 'error' | 'default'; label: string; icon: typeof CheckCircle }> = {
    ACTIVE: { variant: 'success', label: 'Connected', icon: CheckCircle },
    PENDING: { variant: 'warning', label: 'Pending', icon: AlertCircle },
    SUSPENDED: { variant: 'error', label: 'Suspended', icon: AlertTriangle },
    DISCONNECTED: { variant: 'default', label: 'Disconnected', icon: Unplug },
  };
  
  const statusConfig = status && config[status] ? config[status] : config.PENDING;
  const { variant, label, icon: Icon } = statusConfig;
  
  return (
    <Badge variant={variant} className="flex items-center gap-1">
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
};

// ==================== COPY BUTTON COMPONENT ====================

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <button
      onClick={handleCopy}
      className="p-1.5 rounded-md hover:bg-gray-700 transition-colors text-gray-400 hover:text-white"
      title="Copy to clipboard"
    >
      {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
    </button>
  );
};

// ==================== PROVIDER CARD COMPONENT ====================

const ProviderCard = ({ 
  name, 
  description, 
  logo, 
  selected, 
  onClick 
}: { 
  provider: SmsProviderType;
  name: string;
  description: string;
  logo: React.ReactNode;
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`
      relative p-4 rounded-xl border-2 transition-all duration-200 text-left w-full h-full
      ${selected 
        ? 'border-orange-500 bg-orange-500/10' 
        : 'border-gray-700 bg-[#0F1419] hover:border-gray-600 hover:bg-[#1A2332]'
      }
    `}
  >
    {selected && (
      <div className="absolute top-3 right-3">
        <CheckCircle className="w-4 h-4 text-orange-500" />
      </div>
    )}
    <div className="flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${selected ? 'bg-orange-500/20' : 'bg-gray-800'}`}>
        {logo}
      </div>
      <div className="min-w-0 flex-1">
        <h4 className={`text-sm font-semibold leading-tight ${selected ? 'text-orange-400' : 'text-white'}`}>
          {name}
        </h4>
        <p className="text-[11px] text-gray-500 leading-tight mt-0.5 truncate">{description}</p>
      </div>
    </div>
  </button>
);

// ==================== MAIN COMPONENT ====================

const SmsSettingsPage = () => {
  const [account, setAccount] = useState<SmsAccountType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSecrets, setShowSecrets] = useState(false);
  const [testing, setTesting] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<SmsProviderType>('EOCEAN');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm<SmsFormData>({
    resolver: zodResolver(smsSchema),
    defaultValues: {
      provider: 'EOCEAN',
      apiKey: '',
      apiSecret: '',
      accountSid: '',
      senderId: '',
      baseUrl: '',
    },
  });

  const watchProvider = watch('provider');

  // Load existing account on mount
  const loadAccount = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getSmsAccount();
      // Only set account if we have valid data with an id
      if (data && data.id) {
        setAccount(data);
        setSelectedProvider(data.provider as SmsProviderType);
        reset({
          provider: data.provider as SmsProviderType,
          apiKey: '', // Don't show existing API key for security
          apiSecret: '',
          accountSid: '',
          senderId: data.senderId,
          baseUrl: data.baseUrl || '',
        });
      } else {
        setAccount(null);
      }
    } catch (err) {
      console.error('Failed to load SMS account:', err);
      setAccount(null);
    } finally {
      setLoading(false);
    }
  }, [reset]);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  const onSubmit = async (data: SmsFormData) => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        provider: data.provider as ApiSmsProviderType,
        apiKey: data.apiKey,
        apiSecret: data.apiSecret || undefined,
        accountSid: data.accountSid || undefined,
        senderId: data.senderId,
        baseUrl: data.baseUrl || undefined,
      };

      if (account) {
        // Update existing account
        const updated = await updateSmsAccount(payload);
        setAccount(updated);
        setSuccess('SMS integration updated successfully!');
      } else {
        // Create new account
        const created = await createSmsAccount(payload);
        setAccount(created);
        setSuccess('SMS integration configured successfully!');
      }
      
      // Clear sensitive fields after save
      setValue('apiKey', '');
      setValue('apiSecret', '');
      setValue('accountSid', '');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save SMS settings. Please try again.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await testSmsConnection();
      if (result.success) {
        setSuccess(result.message);
        // Refresh account to get updated status
        await loadAccount();
      } else {
        setError(result.message);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Connection test failed. Please check your credentials.';
      setError(message);
    } finally {
      setTesting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!account) return;
    
    if (!window.confirm('Are you sure you want to delete your SMS integration? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      await deleteSmsAccount();
      setAccount(null);
      reset({
        provider: 'EOCEAN',
        apiKey: '',
        apiSecret: '',
        accountSid: '',
        senderId: '',
        baseUrl: '',
      });
      setSelectedProvider('EOCEAN');
      setSuccess('SMS integration removed successfully.');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete SMS account.';
      setError(message);
    } finally {
      setDeleting(false);
    }
  };

  // Pakistani Providers
  const pakistaniProviders: Array<{
    provider: SmsProviderType;
    name: string;
    description: string;
    logo: React.ReactNode;
    country: string;
  }> = [
    {
      provider: 'EOCEAN',
      name: 'eocean',
      description: 'Pakistan\'s leading CPaaS',
      logo: <Globe className="w-5 h-5 text-green-400" />,
      country: 'PK',
    },
    {
      provider: 'JAZZ',
      name: 'Jazz SMS',
      description: 'Mobilink SMS Gateway',
      logo: <Phone className="w-5 h-5 text-red-500" />,
      country: 'PK',
    },
    {
      provider: 'TELENOR',
      name: 'Telenor',
      description: 'Telenor Pakistan SMS',
      logo: <Phone className="w-5 h-5 text-blue-500" />,
      country: 'PK',
    },
    {
      provider: 'UFONE',
      name: 'Ufone',
      description: 'Ufone Business SMS',
      logo: <Phone className="w-5 h-5 text-orange-500" />,
      country: 'PK',
    },
    {
      provider: 'ZONG',
      name: 'Zong',
      description: 'Zong 4G SMS Gateway',
      logo: <Phone className="w-5 h-5 text-purple-500" />,
      country: 'PK',
    },
    {
      provider: 'JEEO_SMS',
      name: 'JEEO SMS',
      description: 'Bulk SMS Pakistan',
      logo: <MessageSquare className="w-5 h-5 text-teal-400" />,
      country: 'PK',
    },
    {
      provider: 'OUTREACH',
      name: 'Outreach',
      description: 'Enterprise SMS Pakistan',
      logo: <Zap className="w-5 h-5 text-indigo-400" />,
      country: 'PK',
    },
    {
      provider: 'SMS4CONNECT',
      name: 'SMS4Connect',
      description: 'SMS Marketing Platform',
      logo: <MessageSquare className="w-5 h-5 text-cyan-400" />,
      country: 'PK',
    },
    {
      provider: 'BROADNET',
      name: 'BroadNet',
      description: 'Business Communications',
      logo: <Globe className="w-5 h-5 text-emerald-400" />,
      country: 'PK',
    },
  ];

  // International Providers
  const internationalProviders: Array<{
    provider: SmsProviderType;
    name: string;
    description: string;
    logo: React.ReactNode;
    country: string;
  }> = [
    {
      provider: 'TWILIO',
      name: 'Twilio',
      description: 'Global cloud comms',
      logo: <MessageSquare className="w-5 h-5 text-red-400" />,
      country: 'US',
    },
    {
      provider: 'VONAGE',
      name: 'Vonage',
      description: 'Enterprise messaging',
      logo: <Phone className="w-5 h-5 text-purple-400" />,
      country: 'US',
    },
    {
      provider: 'MESSAGEBIRD',
      name: 'MessageBird',
      description: 'Omnichannel platform',
      logo: <Zap className="w-5 h-5 text-yellow-400" />,
      country: 'NL',
    },
    {
      provider: 'PLIVO',
      name: 'Plivo',
      description: 'Cloud communications',
      logo: <Phone className="w-5 h-5 text-green-500" />,
      country: 'US',
    },
    {
      provider: 'SINCH',
      name: 'Sinch',
      description: 'Customer engagement',
      logo: <MessageSquare className="w-5 h-5 text-pink-400" />,
      country: 'SE',
    },
    {
      provider: 'INFOBIP',
      name: 'Infobip',
      description: 'Global messaging',
      logo: <Globe className="w-5 h-5 text-orange-400" />,
      country: 'HR',
    },
    {
      provider: 'CUSTOM',
      name: 'Custom Provider',
      description: 'Your own SMS API',
      logo: <Settings className="w-5 h-5 text-gray-400" />,
      country: 'GLOBAL',
    },
  ];


  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading SMS settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500/20 to-green-600/10 flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-green-500" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-semibold text-white">SMS Integration</h2>
            <p className="text-sm text-(--color-muted-gray)">Configure SMS API for messaging</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {account && <StatusBadge status={account.status} />}
          {account && (
            <Button
              type="button"
              variant="danger"
              size="sm"
              onClick={handleDeleteAccount}
              isLoading={deleting}
            >
              <Unplug className="w-4 h-4" />
              Disconnect
            </Button>
          )}
        </div>
      </div>

      {/* Connected Account Info */}
      {account && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#0F1419] border border-gray-800 rounded-xl p-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500/20 to-green-600/10 flex items-center justify-center">
                <Phone className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Connected Provider</p>
                <p className="text-lg font-semibold text-white">{account.provider}</p>
              </div>
              <div className="border-l border-gray-700 pl-4 ml-4">
                <p className="text-sm text-gray-400">Sender ID</p>
                <p className="text-lg font-semibold text-white">{account.senderId}</p>
              </div>
              {account.accountBalance && (
                <div className="border-l border-gray-700 pl-4 ml-4">
                  <p className="text-sm text-gray-400">Balance</p>
                  <p className="text-lg font-semibold text-green-400">{account.accountBalance}</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Info Banner */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/20 rounded-xl p-4"
      >
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center shrink-0">
            <Info className="w-4 h-4 text-green-400" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-green-400 mb-1">
              {account ? 'Update SMS Configuration' : 'SMS Integration Setup'}
            </h3>
            <p className="text-xs text-gray-400">
              {account
                ? 'Update your SMS provider credentials. Leave API key/secret blank to keep existing values.'
                : 'Connect your SMS provider to send bulk SMS campaigns, OTPs, and notifications. We support popular Pakistani providers like Jazz, Telenor, Zong, Ufone, and eocean, plus international providers like Twilio and Vonage.'
              }
            </p>
          </div>
        </div>
      </motion.div>

      {/* Success/Error Messages */}
      {success && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-3"
        >
          <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
          <p className="text-sm text-emerald-400">{success}</p>
        </motion.div>
      )}

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3"
        >
          <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
          <p className="text-sm text-red-400">{error}</p>
        </motion.div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Provider Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#0F1419] border border-gray-800 rounded-xl p-5"
        >
          {/* Pakistani Providers */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Server className="w-4 h-4 text-green-400" />
              <h3 className="text-sm font-medium text-white">Pakistani SMS Providers</h3>
              <Badge className="bg-green-500/20 text-green-400 text-[10px] px-1.5 py-0.5">
                Local
              </Badge>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {pakistaniProviders.map((p) => (
                <ProviderCard
                  key={p.provider}
                  {...p}
                  selected={selectedProvider === p.provider}
                  onClick={() => setSelectedProvider(p.provider)}
                />
              ))}
            </div>
          </div>

          {/* International Providers */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-4 h-4 text-blue-400" />
              <h3 className="text-sm font-medium text-white">International Providers</h3>
              <Badge className="bg-blue-500/20 text-blue-400 text-[10px] px-1.5 py-0.5">
                Global
              </Badge>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {internationalProviders.map((p) => (
                <ProviderCard
                  key={p.provider}
                  {...p}
                  selected={selectedProvider === p.provider}
                  onClick={() => setSelectedProvider(p.provider)}
                />
              ))}
            </div>
          </div>
        </motion.div>

        {/* API Credentials */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[#0F1419] border border-gray-800 rounded-xl p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Key className="w-4 h-4 text-orange-400" />
              <h3 className="text-sm font-medium text-white">API Credentials</h3>
            </div>
            <button
              type="button"
              onClick={() => setShowSecrets(!showSecrets)}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors"
            >
              {showSecrets ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {showSecrets ? 'Hide' : 'Show'} secrets
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="API Key"
              placeholder="Enter your API key"
              type={showSecrets ? 'text' : 'password'}
              {...register('apiKey')}
              error={errors.apiKey?.message}
              leftIcon={<Key className="w-4 h-4" />}
            />

            {/* Show API Secret for providers that need it */}
            {(['TWILIO', 'VONAGE', 'PLIVO', 'JAZZ', 'TELENOR', 'UFONE', 'ZONG', 'OUTREACH', 'BROADNET', 'CUSTOM'] as SmsProviderType[]).includes(watchProvider) && (
              <Input
                label={watchProvider === 'TWILIO' ? 'Auth Token' : 'API Secret'}
                placeholder={watchProvider === 'TWILIO' ? 'Enter auth token' : 'Enter API secret (optional)'}
                type={showSecrets ? 'text' : 'password'}
                {...register('apiSecret')}
                error={errors.apiSecret?.message}
                leftIcon={<Shield className="w-4 h-4" />}
              />
            )}

            {/* Show Account SID for Twilio and Plivo */}
            {(['TWILIO', 'PLIVO'] as SmsProviderType[]).includes(watchProvider) && (
              <Input
                label="Account SID"
                placeholder="Enter account SID"
                {...register('accountSid')}
                error={errors.accountSid?.message}
                leftIcon={<Server className="w-4 h-4" />}
              />
            )}

            <div>
              <Input
                label="Sender ID"
                placeholder="e.g., AmplifyMsg"
                {...register('senderId')}
                error={errors.senderId?.message}
                leftIcon={<MessageSquare className="w-4 h-4" />}
              />
              <p className="text-xs text-gray-500 mt-1">Max 11 alphanumeric characters</p>
            </div>

            {watchProvider === 'CUSTOM' && (
              <Input
                label="API Base URL"
                placeholder="https://api.yourprovider.com"
                {...register('baseUrl')}
                error={errors.baseUrl?.message}
                leftIcon={<Link2 className="w-4 h-4" />}
              />
            )}
          </div>

          {/* Form Actions - Inside API Credentials Card */}
          <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-700">
            {/* Only show Test Connection if account exists */}
            {account ? (
              <Button
                type="button"
                variant="secondary"
                onClick={handleTestConnection}
                isLoading={testing}
                disabled={saving}
                leftIcon={<TestTube className="w-4 h-4" />}
              >
                Test Connection
              </Button>
            ) : (
              <div /> 
            )}
            
            <Button
              type="submit"
              isLoading={saving}
              disabled={testing || loading}
              leftIcon={<Save className="w-4 h-4" />}
            >
              {account ? 'Update Configuration' : 'Save Configuration'}
            </Button>
          </div>
        </motion.div>
      </form>

        {/* Provider Documentation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#0F1419] border border-gray-800 rounded-xl p-5"
        >
          <div className="flex items-center gap-2 mb-4">
            <ExternalLink className="w-4 h-4 text-blue-400" />
            <h3 className="text-sm font-medium text-white">Provider Resources</h3>
          </div>

          {/* Pakistani Providers Documentation */}
          {selectedProvider === 'EOCEAN' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400">
                eocean is Pakistan's leading CPaaS provider, offering SMS, WhatsApp, Voice, and RCS messaging APIs.
              </p>
              <div className="flex flex-wrap gap-2">
                <a href="https://eocean.net" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300 transition-colors">
                  <Globe className="w-3.5 h-3.5" /> Website
                </a>
                <a href="https://eocean.net/docs" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300 transition-colors">
                  <ExternalLink className="w-3.5 h-3.5" /> Documentation
                </a>
              </div>
            </div>
          )}

          {selectedProvider === 'JAZZ' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400">
                Jazz (Mobilink) is Pakistan's largest telecom operator, offering enterprise SMS gateway services for bulk messaging.
              </p>
              <div className="flex flex-wrap gap-2">
                <a href="https://jazz.com.pk/business" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300 transition-colors">
                  <Globe className="w-3.5 h-3.5" /> Jazz Business
                </a>
              </div>
            </div>
          )}

          {selectedProvider === 'TELENOR' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400">
                Telenor Pakistan provides enterprise SMS solutions with high delivery rates and reliable messaging infrastructure.
              </p>
              <div className="flex flex-wrap gap-2">
                <a href="https://www.telenor.com.pk/business" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300 transition-colors">
                  <Globe className="w-3.5 h-3.5" /> Telenor Business
                </a>
              </div>
            </div>
          )}

          {selectedProvider === 'UFONE' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400">
                Ufone offers business SMS services with competitive pricing and nationwide coverage across Pakistan.
              </p>
              <div className="flex flex-wrap gap-2">
                <a href="https://www.ufone.com/business" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300 transition-colors">
                  <Globe className="w-3.5 h-3.5" /> Ufone Business
                </a>
              </div>
            </div>
          )}

          {selectedProvider === 'ZONG' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400">
                Zong 4G provides high-speed SMS gateway with excellent delivery rates and enterprise-grade reliability.
              </p>
              <div className="flex flex-wrap gap-2">
                <a href="https://www.zong.com.pk/business" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300 transition-colors">
                  <Globe className="w-3.5 h-3.5" /> Zong Business
                </a>
              </div>
            </div>
          )}

          {selectedProvider === 'JEEO_SMS' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400">
                JEEO SMS is a popular Pakistani bulk SMS provider offering affordable messaging solutions for businesses.
              </p>
              <div className="flex flex-wrap gap-2">
                <a href="https://jeeosms.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300 transition-colors">
                  <Globe className="w-3.5 h-3.5" /> JEEO SMS
                </a>
              </div>
            </div>
          )}

          {selectedProvider === 'OUTREACH' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400">
                Outreach Pakistan provides enterprise communication solutions including SMS, WhatsApp, and voice services.
              </p>
              <div className="flex flex-wrap gap-2">
                <a href="https://outreach.pk" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300 transition-colors">
                  <Globe className="w-3.5 h-3.5" /> Outreach
                </a>
              </div>
            </div>
          )}

          {selectedProvider === 'SMS4CONNECT' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400">
                SMS4Connect offers reliable SMS marketing platform with delivery tracking and campaign management features.
              </p>
              <div className="flex flex-wrap gap-2">
                <a href="https://sms4connect.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300 transition-colors">
                  <Globe className="w-3.5 h-3.5" /> SMS4Connect
                </a>
              </div>
            </div>
          )}

          {selectedProvider === 'BROADNET' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400">
                BroadNet Technologies offers comprehensive business communication solutions including bulk SMS services.
              </p>
              <div className="flex flex-wrap gap-2">
                <a href="https://broadnet.pk" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300 transition-colors">
                  <Globe className="w-3.5 h-3.5" /> BroadNet
                </a>
              </div>
            </div>
          )}

          {/* International Providers Documentation */}
          {selectedProvider === 'TWILIO' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400">
                Twilio is a global cloud communications platform providing programmable SMS, voice, and video APIs.
              </p>
              <div className="flex flex-wrap gap-2">
                <a href="https://www.twilio.com/docs/sms" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300 transition-colors">
                  <ExternalLink className="w-3.5 h-3.5" /> SMS Documentation
                </a>
                <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300 transition-colors">
                  <Key className="w-3.5 h-3.5" /> Twilio Console
                </a>
              </div>
            </div>
          )}

          {selectedProvider === 'VONAGE' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400">
                Vonage (formerly Nexmo) provides enterprise-grade messaging APIs for SMS and voice communications.
              </p>
              <div className="flex flex-wrap gap-2">
                <a href="https://developer.vonage.com/messaging/sms/overview" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300 transition-colors">
                  <ExternalLink className="w-3.5 h-3.5" /> SMS Documentation
                </a>
              </div>
            </div>
          )}

          {selectedProvider === 'MESSAGEBIRD' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400">
                MessageBird offers omnichannel messaging solutions for SMS, WhatsApp, and other channels.
              </p>
              <div className="flex flex-wrap gap-2">
                <a href="https://developers.messagebird.com/api/sms-messaging/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300 transition-colors">
                  <ExternalLink className="w-3.5 h-3.5" /> SMS API Docs
                </a>
              </div>
            </div>
          )}

          {selectedProvider === 'PLIVO' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400">
                Plivo offers cloud communications APIs for SMS and voice with competitive global pricing.
              </p>
              <div className="flex flex-wrap gap-2">
                <a href="https://www.plivo.com/docs/sms/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300 transition-colors">
                  <ExternalLink className="w-3.5 h-3.5" /> SMS Documentation
                </a>
              </div>
            </div>
          )}

          {selectedProvider === 'SINCH' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400">
                Sinch provides customer engagement cloud solutions with SMS, voice, and video capabilities.
              </p>
              <div className="flex flex-wrap gap-2">
                <a href="https://developers.sinch.com/docs/sms/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300 transition-colors">
                  <ExternalLink className="w-3.5 h-3.5" /> SMS Documentation
                </a>
              </div>
            </div>
          )}

          {selectedProvider === 'INFOBIP' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400">
                Infobip is a global cloud communications platform with SMS, email, voice, and omnichannel solutions.
              </p>
              <div className="flex flex-wrap gap-2">
                <a href="https://www.infobip.com/docs/sms" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300 transition-colors">
                  <ExternalLink className="w-3.5 h-3.5" /> SMS Documentation
                </a>
              </div>
            </div>
          )}

          {selectedProvider === 'CUSTOM' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400">
                Configure a custom SMS provider by entering your API credentials and base URL.
                Your provider should support standard REST API for sending SMS.
              </p>
            </div>
          )}
        </motion.div>

        {/* Webhook Configuration */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-[#0F1419] border border-gray-800 rounded-xl p-5"
        >
          <div className="flex items-center gap-2 mb-4">
            <Link2 className="w-4 h-4 text-purple-400" />
            <h3 className="text-sm font-medium text-white">Webhook Configuration</h3>
            <Badge variant="info" size="sm">Optional</Badge>
          </div>

          <p className="text-xs text-gray-500 mb-4">
            Configure these endpoints in your SMS provider to receive delivery reports and incoming messages.
          </p>

          <div className="space-y-3">
            {/* Webhook URL */}
            <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
              <p className="text-xs text-gray-500 mb-1">Webhook URL (Delivery Reports)</p>
              <div className="flex items-center justify-between gap-2">
                <code className="text-xs text-green-400 font-mono break-all">
                  {`${import.meta.env.VITE_API_BASE_URL}/${import.meta.env.VITE_API_PREFIX}/${import.meta.env.VITE_API_VERSION}/webhook/sms/delivery`}
                </code>
                <CopyButton text={`${import.meta.env.VITE_API_BASE_URL}/${import.meta.env.VITE_API_PREFIX}/${import.meta.env.VITE_API_VERSION}/webhook/sms/delivery`} />
              </div>
            </div>

            {/* Incoming Messages URL */}
            <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
              <p className="text-xs text-gray-500 mb-1">Webhook URL (Incoming Messages)</p>
              <div className="flex items-center justify-between gap-2">
                <code className="text-xs text-green-400 font-mono break-all">
                  {`${import.meta.env.VITE_API_BASE_URL}/${import.meta.env.VITE_API_PREFIX}/${import.meta.env.VITE_API_VERSION}/webhook/sms/incoming`}
                </code>
                <CopyButton text={`${import.meta.env.VITE_API_BASE_URL}/${import.meta.env.VITE_API_PREFIX}/${import.meta.env.VITE_API_VERSION}/webhook/sms/incoming`} />
              </div>
            </div>

            {/* Generic Webhook URL */}
            <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
              <p className="text-xs text-gray-500 mb-1">Generic Webhook URL (All Events)</p>
              <div className="flex items-center justify-between gap-2">
                <code className="text-xs text-green-400 font-mono break-all">
                  {`${import.meta.env.VITE_API_BASE_URL}/${import.meta.env.VITE_API_PREFIX}/${import.meta.env.VITE_API_VERSION}/webhook/sms`}
                </code>
                <CopyButton text={`${import.meta.env.VITE_API_BASE_URL}/${import.meta.env.VITE_API_PREFIX}/${import.meta.env.VITE_API_VERSION}/webhook/sms`} />
              </div>
            </div>

            {/* Verify Token - Only show if account exists */}
            {account && account.webhookVerifyToken && (
              <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
                <p className="text-xs text-gray-500 mb-1">Verify Token</p>
                <div className="flex items-center justify-between gap-2">
                  <code className="text-sm text-white font-mono truncate min-w-0 flex-1">
                    {showSecrets ? account.webhookVerifyToken : '••••••••••••••••••••••••••••••••'}
                  </code>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowSecrets(!showSecrets)}
                      className="p-1.5 rounded-md hover:bg-gray-700 transition-colors text-gray-400 hover:text-white"
                    >
                      {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <CopyButton text={account.webhookVerifyToken} />
                  </div>
                </div>
              </div>
            )}

            {/* Info note */}
            <div className="flex items-start gap-2 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
              <Info className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
              <div className="text-xs text-gray-400">
                <p className="font-medium text-purple-400 mb-1">How to configure webhooks:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Go to your SMS provider's dashboard</li>
                  <li>Find the webhook or callback URL settings</li>
                  <li>Copy and paste the appropriate URL above</li>
                  <li>If required, use the Verify Token for authentication</li>
                </ol>
              </div>
            </div>
          </div>
        </motion.div>

    </div>
  );
};

export default SmsSettingsPage;
